/**
 * Full screen for a slide stage: `toggle()` puts `ref`'s element on screen alone.
 * Uses the Fullscreen API; where the browser refuses (embedded in the BO iframe,
 * no user gesture, unsupported) it falls back to a fixed overlay that covers the
 * viewport — same look, Esc leaves either way.
 */
import { useCallback, useEffect, useState, type CSSProperties, type RefObject } from 'react';

export function useFullscreen(ref: RefObject<HTMLElement | null>) {
  const [native, setNative] = useState(false);
  const [overlay, setOverlay] = useState(false);
  useEffect(() => {
    const onChange = () => setNative(!!document.fullscreenElement && document.fullscreenElement === ref.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, [ref]);
  useEffect(() => {
    if (!overlay) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOverlay(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [overlay]);
  const toggle = useCallback(async () => {
    if (overlay) { setOverlay(false); return; }
    if (document.fullscreenElement) { try { await document.exitFullscreen(); } catch { /* ignore */ } return; }
    const el = ref.current;
    if (!el) return;
    try {
      if (!document.fullscreenEnabled || !el.requestFullscreen) throw new Error('unsupported');
      await el.requestFullscreen();
    } catch {
      setOverlay(true);
    }
  }, [ref, overlay]);
  return { isFs: native || overlay, toggle, supported: true };
}

/** Styles for the full-screen stage: the slide alone, letterboxed in its own background, no viewer chrome. */
export function fullscreenStageStyle(bg: string): { outer: CSSProperties; inner: CSSProperties } {
  return {
    // position:fixed is harmless under the native API (the element is already the top layer) and does the job for the overlay fallback.
    outer: { position: 'fixed', inset: 0, zIndex: 9999, width: '100vw', height: '100vh', display: 'grid', placeItems: 'center', background: bg },
    inner: { width: 'min(100vw, calc(100vh * 16 / 9))' },
  };
}
